import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { Switch } from '@factorialco/f0-react/dist/experimental'
import { PagesPageVersionsDocument, PagesPublishPageDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface PublishPageDialogProps {
  pageId: number
  folderType: string
  pageName: string
  isMandatory?: boolean
  onClose: () => void
  hasContent: boolean
  hasTitle: boolean
}

export const PublishPageDialog = ({
  pageId,
  folderType,
  pageName,
  isMandatory = false,
  onClose,
  hasContent,
  hasTitle,
}: PublishPageDialogProps) => {
  const [requestReadingAcceptance, setRequestReadingAcceptance] = useState(isMandatory)
  const [requiresReacknowledgement, setRequiresReacknowledgement] = useState(false)

  const [publishPage] = useMutation(PagesPublishPageDocument, {
    extractorFn: (data) => data.handbookPagesMutations.publishHandbookpagesPage,
    refetchQueries: [
      {
        query: PagesPageVersionsDocument,
        variables: {
          pageId,
        },
      },
    ],
  })

  const handleConfirmPublish = async () => {
    track(trackingEvents.pages.PagesDocumentsPublished, {
      documents_published_count: 1,
    })

    const result = await publishPage({
      variables: {
        id: pageId,
        mandatory: requestReadingAcceptance,
        requiresReacknowledgement:
          isMandatory && requestReadingAcceptance ? requiresReacknowledgement : undefined,
      },
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('pages.page.publish_page_dialog.success'),
      })
      track(trackingEvents.pages.PagesDocumentsPublishConfirmed, {
        documents_published_count: 1,
      })
      track(trackingEvents.pages.PagesPublishNewPageConfirmation, {
        folder_type: folderType,
        page_name: pageName,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }

    onClose()
  }

  if (!hasContent || !hasTitle) {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('pages.page.publish_page_dialog.missing_content_or_title'),
    })

    onClose()
    return null
  }

  return (
    <F0Dialog
      isOpen={!!pageId}
      onClose={onClose}
      title={i18n.t('pages.page.publish_page_dialog.title')}
      primaryAction={{
        label: i18n.t('pages.page.publish'),
        onClick: handleConfirmPublish,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
      width="sm"
    >
      <Box
        padding="s16"
        border={{ all: { color: 'grey300', width: 's1', style: 'solid' } }}
        borderRadius={{ all: 'abs016' }}
        gap="s8"
        flexDirection="row"
        alignItems="flexStart"
        justifyContent="spaceBetween"
      >
        {isMandatory ? (
          <>
            <Box flexDirection="column">
              <Text.Normal size="200" color="grey1000" weight="medium">
                {i18n.t('pages.page.publish_page_dialog.requires_reacknowledgement')}
              </Text.Normal>
              <Text.Normal size="200" color="grey800">
                {i18n.t('pages.page.publish_page_dialog.requires_reacknowledgement_description')}
              </Text.Normal>
            </Box>
            <Switch
              checked={requiresReacknowledgement}
              onCheckedChange={(checked) => setRequiresReacknowledgement(checked === true)}
            />
          </>
        ) : (
          <>
            <Box flexDirection="column">
              <Text.Normal size="200" color="grey1000" weight="medium">
                {i18n.t('pages.page.publish_page_dialog.request_reading_acceptance')}
              </Text.Normal>
              <Text.Normal size="200" color="grey800">
                {i18n.t('pages.page.publish_page_dialog.request_reading_acceptance_description')}
              </Text.Normal>
            </Box>
            <Switch
              checked={requestReadingAcceptance}
              onCheckedChange={(checked) => setRequestReadingAcceptance(checked === true)}
            />
          </>
        )}
      </Box>
    </F0Dialog>
  )
}
